import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { membersApi } from '@store/rtk/members';
import { selectItemType } from '@src/types';
import { clearFunctionReset } from '@src/store/helpers';

type MembersInfoDataType = {
  [key: string]: string;
};

type MembersBusinessDataType = {
  [key: string]: string;
};

type MembersInfoSliceType = {
  name: string;
  membersInfoData: MembersInfoDataType;
  membersBusinessData: MembersBusinessDataType;
  membersData: {
    memberContract: {
      physicalAddress: Record<string, string>;
      mailingAddress: Record<string, string>;
    };
  } | null;
  pronounsSelectData: Array<selectItemType> | null;
  isMemberSaved: boolean;
  selectedLobs: string | null;
  memberBasicAnalytics: any;
  activeRestrictions: any;
  draftFormValues: any | null;
  draftMemberId: string | number | null;
  isDraftEditing: boolean;
};

const initialState: MembersInfoSliceType = {
  name: '',
  membersInfoData: {},
  membersBusinessData: {},
  membersData: null,
  pronounsSelectData: null,
  isMemberSaved: true,
  selectedLobs: null,
  memberBasicAnalytics: null,
  activeRestrictions: {},
  draftFormValues: null,
  draftMemberId: null,
  isDraftEditing: false,
};

export const membersInfoSlice = createSlice({
  name: 'membersInfo',
  initialState,
  reducers: {
    setName(state, action: PayloadAction<string>) {
      state.name = action.payload;
    },
    setMembersInfoData(
      state,
      action: PayloadAction<{ key: string; value: string }>
    ) {
      state.membersInfoData = {
        ...state.membersInfoData,
        [action.payload.key]: action.payload.value,
      };
    },
    setMembersBusinessData(
      state,
      action: PayloadAction<{ key: string; value: string }>
    ) {
      state.membersBusinessData = {
        ...state.membersBusinessData,
        [action.payload.key]: action.payload.value,
      };
    },
    setIsMemberSaved: (state, { payload }) => {
      state.isMemberSaved = payload;
    },
    setSelectedLobs(state, action: PayloadAction<string | null>) {
      state.selectedLobs = action.payload;
    },
    setMemberDraft: (state, { payload }) => {
      state.draftFormValues = payload.values;
      state.draftMemberId = payload.memberId;
      state.isDraftEditing = payload.isEditing;
    },
    clearMemberDraft: (state) => {
      state.draftFormValues = null;
      state.draftMemberId = null;
      state.isDraftEditing = false;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      membersApi.endpoints.getMemberDetails.matchFulfilled,
      (state: any, { payload }) => {
        const clearData = clearFunctionReset(payload);
        state.membersData = clearData;
      }
    );
    builder
      .addMatcher(
        membersApi.endpoints.getMemberPronouns.matchFulfilled,
        (state, { payload }) => {
          state.pronounsSelectData = payload;
        }
      )
      .addMatcher(
        membersApi.endpoints.basicAnalytics.matchFulfilled,
        (state, { payload }) => {
          state.memberBasicAnalytics = payload;
        }
      )
      .addMatcher(
        membersApi.endpoints.getRestrictedActive.matchFulfilled,
        (state, { payload }) => {
          state.activeRestrictions = payload;
        }
      );
  },
});

export const selectMembersInfo = (state) => state.membersInfo;
export const {
  setName,
  setMembersInfoData,
  setMembersBusinessData,
  setIsMemberSaved,
  setSelectedLobs,
  setMemberDraft,
  clearMemberDraft,
} = membersInfoSlice.actions;
export const membersInfoReducer = membersInfoSlice.reducer;
